import { useState } from 'react';
import {
  findPushPlatform,
  readFileAsText,
  type ApnsCredentialFieldErrors,
  type PushPlatformDetails,
} from '@/features/push-credentials';
import { useNavigationGuard } from '@/services/navigation-guard';
import { isEmptyString } from '@/utils/string';
import { usePushSettingsErrors } from '../hooks';
import { hasConfigError, pushErrorsOrFallback, REMOVE_FAILED_MESSAGE } from '../utils/push-errors';
import type { PushProviderFormProps, PushProviderFormState, PushSaveContext } from '../types';
import { ApnsFieldsView, EMPTY_APNS_FIELDS, type ApnsFields } from './apns-fields-view';
import { useApnsSandbox } from './use-apns-sandbox';
import {
  pushSaveContext,
  usePushCredentialWrites,
  usePushSaveRunner,
  type PushCredentialWrites,
} from './push-credential-writes';
import { useSaveAttempt } from './use-save-attempt';

function apnsFieldErrors({ file, keyId, teamId, bundleId }: ApnsFields): ApnsCredentialFieldErrors {
  const errors: ApnsCredentialFieldErrors = {};
  if (file == null) {
    errors.file = 'Upload a .p8 certificate.';
  }
  if (isEmptyString(keyId)) {
    errors.keyId = "Key ID can't be empty.";
  }
  if (isEmptyString(teamId)) {
    errors.teamId = "Team ID can't be empty.";
  }
  if (isEmptyString(bundleId)) {
    errors.bundleId = "Bundle ID can't be empty.";
  }
  return errors;
}

/** What has been typed and picked, and what a refused save found missing from it. */
function useApnsDraft() {
  const [fields, setFields] = useState<ApnsFields>(EMPTY_APNS_FIELDS);
  const attempt = useSaveAttempt();
  const fieldErrors = apnsFieldErrors(fields);
  const isComplete = Object.keys(fieldErrors).length === 0;

  return {
    fields,
    isComplete,
    /** Null while anything the credential needs is still missing. */
    fileToSave: isComplete ? fields.file : null,
    shownErrors: attempt.hasAttempted ? fieldErrors : {},
    fieldsRef: attempt.fieldsRef,
    refuse: attempt.refuse,
    patch: (patch: Partial<ApnsFields>) => {
      setFields((current) => ({ ...current, ...patch }));
    },
    clear: () => {
      setFields(EMPTY_APNS_FIELDS);
      attempt.reset();
    },
  };
}

function isApnsDirty(fields: ApnsFields, sandbox: boolean, savedSandbox: boolean): boolean {
  const typed = fields.keyId.length + fields.teamId.length + fields.bundleId.length;
  return fields.file != null || typed > 0 || sandbox !== savedSandbox;
}

async function buildApnsPassword({ file, keyId, teamId, bundleId }: ApnsFields): Promise<string> {
  const privateKey = file == null ? null : await readFileAsText(file);
  return JSON.stringify({
    key_id: keyId.trim(),
    team_id: teamId.trim(),
    bundle_id: bundleId.trim(),
    private_key: privateKey,
  });
}

type ApnsWriteInput = {
  writes: PushCredentialWrites;
  platforms: readonly PushPlatformDetails[];
  fields: ApnsFields;
  file: File;
  sandbox: boolean;
};

/** iOS sends through one service, so it comes off Firebase before the Apple key is stored. */
async function writeApnsCredential({ writes, platforms, fields, file, sandbox }: ApnsWriteInput): Promise<void> {
  const password = await buildApnsPassword(fields);
  if (findPushPlatform(platforms, 'ios')?.provider === 'fcm') {
    await writes.setConfig('push_fcm', '');
  }
  await writes.upsert('push_apns', { username: file.name, password, config: sandbox ? 'sandbox' : '' });
}

export function useApnsForm({
  workspaceId,
  platforms,
  credential,
  isEditing,
  isReading,
  canEdit,
  onEditingChange,
  onCancel,
  onSaved,
  onDisabled,
}: PushProviderFormProps): PushProviderFormState {
  const errors = usePushSettingsErrors();
  const writes = usePushCredentialWrites(workspaceId);
  const runSave = usePushSaveRunner(errors.addErrors);
  const draft = useApnsDraft();
  const { fields } = draft;

  const isEnabled = credential != null;
  const isBusy = isReading || writes.isWriting;
  const { sandbox, savedSandbox, sendsToDevelopment, changeSandbox, resetSandbox } = useApnsSandbox({
    platforms,
    isEnabled,
    isEditing,
    writes,
    addErrors: errors.addErrors,
    onEditingChange,
  });

  const isDirty = isApnsDirty(fields, sandbox, savedSandbox);
  useNavigationGuard({ hasUnsavedChanges: isDirty });

  const finishSave = (context: PushSaveContext) => {
    draft.clear();
    onEditingChange(false);
    onSaved(context);
  };

  const save = () => {
    const file = draft.fileToSave;
    if (file == null) {
      draft.refuse();
      return;
    }
    const context = pushSaveContext(platforms, 'ios');
    void runSave({
      write: () => writeApnsCredential({ writes, platforms, fields, file, sandbox }),
      onSaved: () => finishSave(context),
    });
  };

  const cancel = () => {
    draft.clear();
    resetSandbox();
    onCancel();
  };

  const disable = async () => {
    try {
      await writes.remove('push_apns');
    } catch (error) {
      errors.addErrors('config', pushErrorsOrFallback(error, REMOVE_FAILED_MESSAGE));
      return;
    }
    onDisabled();
  };

  return {
    provider: 'apns',
    isEnabled,
    isSaving: writes.isWriting,
    isBusy,
    sandbox: sendsToDevelopment,
    isComplete: draft.isComplete,
    isDirty,
    errors,
    getCredentials: isEnabled ? null : () => buildApnsPassword(fields),
    fields: (
      <ApnsFieldsView
        ref={draft.fieldsRef}
        fields={fields}
        errors={draft.shownErrors}
        sandbox={sandbox}
        savedFileName={credential?.username ?? null}
        isEditing={isEditing}
        canEdit={canEdit}
        isBusy={isBusy}
        isInvalid={hasConfigError(errors.errors)}
        onChange={draft.patch}
        onSandboxChange={changeSandbox}
      />
    ),
    save,
    cancel,
    disable,
  };
}
